import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { formatDate } from "@progress/kendo-intl";
import { process } from "@progress/kendo-data-query";

import LoadingIndicator from "../../components/LoadingIndicator";
import ErrorBanner from "../../components/ErrorBanner";
import SuccessBanner from "../../components/SuccessBanner";
import SalesAdminMappingModal from "./SalesAdminMappingModal";
import SalesAdminMappingImportModal from "./SalesAdminMappingImportModal";
import {
  useQueryGetSalesAdminMappings,
  useMutatePostSalesAdminMappingRemove,
} from "../../data/services/salesAdminMappingService";
import { normalizeUserId } from "../../utils/salesAdminUserIds";

/**
 * Sales Admin Mapping admin page.
 *
 * Maintains which BDMs a PrismHR "Sales Admin" user supports. A mapped Sales
 * Admin's client visibility is scoped to those BDMs' clients (or all clients,
 * when allowAllClients is set) instead of their own PrismHR assignments.
 *
 * Unlike Report Exclusions, this list is global - there is no OE period - so
 * there is no period picker here.
 */
const SalesAdminMapping = () => {
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [editSalesAdminUserId, setEditSalesAdminUserId] = React.useState(null);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [gridDataState, setGridDataState] = React.useState({
    skip: 0,
    take: 20,
  });

  const { isLoading: listLoading, data: listResponse } =
    useQueryGetSalesAdminMappings(includeInactive);

  // A second, always-includeInactive list purely to feed the import modal's
  // preview. If the importer only saw the active grid rows, a soft-deleted
  // pair would preview as Add instead of Reactivate - a misleading preview,
  // not a data bug, since the server still returns the true outcome. This is
  // a distinct react-query key from the grid's own toggle above, so both
  // cache independently.
  const { data: allMappingsResponse } = useQueryGetSalesAdminMappings(true);

  const removeHandler = useMutatePostSalesAdminMappingRemove();

  const rows = listResponse?.data ?? [];
  // Memoized so it is a stable dependency for editModalMappings below - the
  // bare `?? []` produced a fresh array on every render whenever the query
  // had no data yet, which is exactly when the modal's preload effect must
  // not be re-triggered.
  const allMappings = React.useMemo(
    () => allMappingsResponse?.data ?? [],
    [allMappingsResponse]
  );

  const handleRemove = async (dataItem) => {
    try {
      await removeHandler.mutateAsync({
        salesAdminUserId: dataItem.salesAdminUserId,
        bdmUserId: dataItem.bdmUserId,
      });
    } catch {
      // Surfaced by <ErrorBanner handler={removeHandler} />.
    }
  };

  const dateCreatedCell = (props) => {
    // new Date(null) is the epoch and new Date(undefined) is Invalid Date, so
    // an absent value would otherwise render as 01/01/1970 or "Invalid Date".
    const raw = props.dataItem.dateCreated;
    const parsed = raw ? new Date(raw) : null;
    const isValid = parsed !== null && !Number.isNaN(parsed.getTime());

    return <td>{isValid ? formatDate(parsed, "MM/dd/yyyy") : ""}</td>;
  };

  const allowAllClientsCell = (props) => (
    <td>{props.dataItem.allowAllClients ? "Yes" : "No"}</td>
  );

  const statusCell = (props) => (
    <td>{props.dataItem.active ? "Mapped" : "Removed"}</td>
  );

  const actionCell = (props) => {
    const dataItem = props.dataItem;

    if (!dataItem.active) {
      return <td />;
    }

    // A null-BdmUserId row is the allow-all row: RemoveSalesAdminMapping's
    // validator requires a non-empty BdmUserId, so a Remove button here would
    // always 400. Route to the edit modal instead - its "Remove All
    // Mappings" is scoped to this Sales Admin and already behind a
    // confirmation dialog - rather than wiring a second, redundant
    // confirmation flow into this grid for the same action.
    const canRemoveRow = !!dataItem.bdmUserId;

    return (
      <td>
        <Button
          fillMode={"flat"}
          onClick={() => setEditSalesAdminUserId(dataItem.salesAdminUserId)}
        >
          {"Edit"}
        </Button>
        {canRemoveRow && (
          <Button
            themeColor={"error"}
            fillMode={"flat"}
            onClick={() => handleRemove(dataItem)}
          >
            {"Remove"}
          </Button>
        )}
      </td>
    );
  };

  // The edit modal wants only the one Sales Admin's own rows (unfiltered by
  // active - it filters active !== false itself).
  //
  // Memoized because the modal's preload effect depends on this array: an
  // inline .filter() produces a new identity on every render of this page, so
  // any re-render while the modal is open (a refetchOnReconnect, say) re-ran
  // that effect and silently reset the admin's in-progress BDM selections to
  // the stored set. Compared through the shared normalizer for the usual
  // reason - stored ids are lowercased, ids from elsewhere are not.
  const editModalMappings = React.useMemo(() => {
    if (!editSalesAdminUserId) return [];

    const normalizedEditId = normalizeUserId(editSalesAdminUserId);

    return allMappings.filter(
      (mapping) => normalizeUserId(mapping.salesAdminUserId) === normalizedEditId
    );
  }, [allMappings, editSalesAdminUserId]);

  return (
    <div style={{ padding: "20px" }}>
      <h2>Sales Admin Mapping</h2>
      <p>
        A Sales Admin sees only the clients of the BDMs mapped to them here, and
        can run the Full Rate Benefit Menus, Rate Menu Comparisons, and Benefit
        Employee Menus reports read-only. They cannot create or edit rate models.
        Mapping a Sales Admin replaces whatever clients they would otherwise see,
        including their own assignments. An allow-all Sales Admin may still see
        empty results on some report screens; this is a known, accepted
        limitation, not a mapping error.
      </p>

      <ErrorBanner handler={removeHandler} />
      <SuccessBanner
        handler={removeHandler}
        message={"Mapping removed."}
      />

      <div style={{ display: "flex", gap: "20px", alignItems: "flex-end" }}>
        <div>
          <Checkbox
            label={"Show removed"}
            value={includeInactive}
            onChange={(e) => setIncludeInactive(e.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        {listLoading ? (
          <LoadingIndicator />
        ) : (
          <Grid
            data={process(rows, gridDataState)}
            {...gridDataState}
            onDataStateChange={(event) => setGridDataState(event.dataState)}
            sortable={true}
            filterable={true}
            pageable={{ pageSizes: [20, 50, 100] }}
          >
            <GridToolbar>
              <Button
                themeColor={"primary"}
                onClick={() => {
                  setEditSalesAdminUserId(null);
                  setShowAddModal(true);
                }}
              >
                {"Add Mapping"}
              </Button>
              <Button onClick={() => setShowImportModal(true)}>
                {"Import"}
              </Button>
            </GridToolbar>
            <Column
              field="salesAdminName"
              title="Sales Admin"
              cell={(props) => (
                <td>
                  {props.dataItem.salesAdminName} {props.dataItem.salesAdminUserId}
                </td>
              )}
            />
            <Column
              field="bdmName"
              title="BDM"
              cell={(props) => (
                <td>
                  {props.dataItem.bdmName} {props.dataItem.bdmUserId}
                </td>
              )}
            />
            <Column
              field="allowAllClients"
              title="Allow All Clients"
              width={150}
              cell={allowAllClientsCell}
            />
            <Column
              field="dateCreated"
              title="Date Created"
              width={150}
              cell={dateCreatedCell}
            />
            <Column field="createdBy" title="Created By" width={180} />
            {includeInactive && (
              <Column
                field="active"
                title="Status"
                width={120}
                cell={statusCell}
              />
            )}
            <Column title="Actions" width={160} cell={actionCell} />
          </Grid>
        )}
      </div>

      {(showAddModal || editSalesAdminUserId) && (
        <SalesAdminMappingModal
          salesAdminUserId={editSalesAdminUserId}
          currentMappings={editModalMappings}
          onClose={() => {
            setShowAddModal(false);
            setEditSalesAdminUserId(null);
          }}
          onSubmit={() => {
            setShowAddModal(false);
            setEditSalesAdminUserId(null);
          }}
        />
      )}

      {showImportModal && (
        <SalesAdminMappingImportModal
          currentMappings={allMappings}
          onClose={() => setShowImportModal(false)}
          onSubmit={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
};

export default SalesAdminMapping;
